<template>
  <v-card class="busca-funcionario" outlined elevation="0">
    <div class="busca-funcionario__cabecalho">
      <div class="busca-funcionario__identificacao">
        <v-avatar size="42" class="busca-funcionario__icone">
          <v-icon color="primary">mdi-account-search-outline</v-icon>
        </v-avatar>
        <div class="min-width-0">
          <h5 class="font-weight-bold text-h5 text-typo mb-1">
            {{ title }}
          </h5>
          <p class="text-sm text-body mb-0">
            {{ subtitle }}
          </p>
        </div>
      </div>

      <div class="busca-funcionario__indicadores" aria-live="polite">
        <v-chip v-if="internalSelected.length" small outlined color="success">
          <v-icon left small>mdi-check-circle-outline</v-icon>
          {{ selectedCountLabel }}
        </v-chip>
        <v-chip v-if="showResultCount" small outlined color="primary">
          <v-icon left small>mdi-format-list-bulleted</v-icon>
          {{ resultCountLabel }}
        </v-chip>
      </div>
    </div>

    <div class="busca-funcionario__filtros" role="search">
      <v-row class="busca-funcionario__campos">
        <v-col cols="12" md="6">
          <v-text-field
            ref="nomeInput"
            v-model.trim="filters.nome"
            label="Nome do funcionário"
            placeholder="Digite parte do nome"
            dense
            solo
            filled
            clearable
            hide-details
            :disabled="loading"
            @keydown="onSearchShortcut"
          >
            <template slot="prepend-inner">
              <v-icon color="#8392ab" size="18">mdi-account-outline</v-icon>
            </template>
          </v-text-field>
        </v-col>

        <v-col cols="12" md="3">
          <v-text-field
            v-model.trim="filters.matricula"
            label="Matrícula"
            placeholder="Digite a matrícula"
            dense
            solo
            filled
            clearable
            hide-details
            :disabled="loading"
            @keydown="onSearchShortcut"
          >
            <template slot="prepend-inner">
              <v-icon color="#8392ab" size="18">
                mdi-card-account-details-outline
              </v-icon>
            </template>
          </v-text-field>
        </v-col>

        <v-col cols="12" md="3" class="busca-funcionario__acoes-busca">
          <v-btn
            class="
              busca-funcionario__botao
              font-weight-bold
              text-none
              btn-primary
              bg-gradient-primary
              px-5
            "
            height="39"
            type="button"
            :loading="loading"
            aria-label="Buscar funcionários"
            @click="onSearch"
          >
            <v-icon left size="18">mdi-magnify</v-icon>
            Buscar
          </v-btn>
          <v-btn
            class="
              busca-funcionario__botao
              font-weight-bold
              text-none
              btn-ls btn-secondary
              bg-gradient-light
              px-4
            "
            height="39"
            type="button"
            :disabled="(!hasFilters && !searched) || loading"
            aria-label="Limpar filtros da busca de funcionários"
            @click="onClear"
          >
            <v-icon left size="18">mdi-filter-remove-outline</v-icon>
            Limpar
          </v-btn>
        </v-col>
      </v-row>

      <div class="busca-funcionario__dica">
        <v-icon size="15" color="#8392ab" class="mr-1">
          mdi-keyboard-outline
        </v-icon>
        Atalho para buscar: Ctrl/⌘ + Shift + Enter.
      </div>
    </div>

    <!-- Cor explícita: o tema do Vuetify está desligado no projeto (sem ela, o texto some). -->
    <v-alert
      v-if="infoText"
      type="info"
      color="blue darken-2"
      dense
      text
      border="left"
      class="busca-funcionario__aviso"
    >
      {{ infoText }}
    </v-alert>

    <v-alert
      v-if="errorMessage"
      type="error"
      color="red darken-2"
      dense
      text
      border="left"
      class="busca-funcionario__erro"
    >
      <div class="d-flex flex-wrap align-center justify-space-between">
        <span>{{ errorMessage }}</span>
        <v-btn
          text
          small
          type="button"
          color="error"
          :loading="loading"
          @click="onSearch"
        >
          Tentar novamente
        </v-btn>
      </div>
    </v-alert>

    <div
      v-if="showLocalResultFilter"
      class="busca-funcionario__resultados-topo"
    >
      <div class="busca-funcionario__secao-titulo mb-0">
        <v-icon size="18" color="primary" class="mr-2">
          mdi-account-multiple-outline
        </v-icon>
        <span>Resultados da busca</span>
      </div>
      <v-text-field
        v-model.trim="tableSearch"
        label="Filtrar resultados exibidos"
        dense
        solo
        filled
        clearable
        hide-details
        class="busca-funcionario__filtro-local"
      >
        <template slot="prepend-inner">
          <v-icon color="#8392ab" size="18">mdi-filter-outline</v-icon>
        </template>
      </v-text-field>
    </div>

    <div
      v-if="multiple && showSelectionActions"
      class="busca-funcionario__selecao-acoes"
    >
      <div class="busca-funcionario__selecao-contagem">
        <v-icon color="primary" size="20" class="mr-2">
          mdi-checkbox-marked-circle-outline
        </v-icon>
        <span>{{ selectedCountLabel }}</span>
      </div>
      <div class="busca-funcionario__selecao-botoes">
        <v-btn
          small
          text
          type="button"
          color="primary"
          class="font-weight-bold text-none"
          :disabled="!items.length || allSelected"
          @click="selectAll"
        >
          <v-icon left small>mdi-select-all</v-icon>
          Selecionar todos
        </v-btn>
        <v-btn
          small
          text
          type="button"
          color="secondary"
          class="font-weight-bold text-none"
          :disabled="!internalSelected.length"
          @click="clearSelection"
        >
          <v-icon left small>mdi-checkbox-blank-off-outline</v-icon>
          Limpar seleção
        </v-btn>
      </div>
    </div>

    <section
      v-if="showSelectedCards"
      class="busca-funcionario__selecionados"
      aria-live="polite"
      aria-label="Funcionários selecionados"
    >
      <div class="busca-funcionario__selecionados-cabecalho">
        <div class="busca-funcionario__selecionados-titulo">
          <v-icon color="success" size="22">mdi-check-decagram</v-icon>
          <div>
            <strong>{{ selectedSectionTitle }}</strong>
            <span>{{ selectedSectionHint }}</span>
          </div>
        </div>
        <v-chip small color="success" outlined class="font-weight-bold">
          {{ selectedCountLabel }}
        </v-chip>
      </div>

      <div class="busca-funcionario__selecionados-grid" role="list">
        <article
          v-for="(item, index) in internalSelected"
          :key="getItemKey(item) || index"
          class="busca-funcionario__selecionado-card"
          role="listitem"
          :title="formatLabel(item)"
        >
          <div class="busca-funcionario__selecionado-avatar">
            <v-avatar size="40">
              <span>{{ getFuncionarioInitials(item) }}</span>
            </v-avatar>
            <span class="busca-funcionario__selecionado-check">
              <v-icon color="white" size="11">mdi-check</v-icon>
            </span>
          </div>

          <div class="busca-funcionario__selecionado-conteudo">
            <strong class="busca-funcionario__selecionado-nome">
              {{ getFuncionarioName(item) }}
            </strong>
            <span
              v-if="getFuncionarioRegistration(item)"
              class="busca-funcionario__selecionado-matricula"
            >
              <v-icon size="14">mdi-card-account-details-outline</v-icon>
              Matrícula {{ getFuncionarioRegistration(item) }}
            </span>
            <span v-else class="busca-funcionario__selecionado-matricula">
              <v-icon size="14">mdi-account-check-outline</v-icon>
              Selecionado
            </span>
          </div>

          <v-btn
            v-if="showChips"
            icon
            small
            type="button"
            color="error"
            class="busca-funcionario__selecionado-remover"
            :title="`Remover ${getFuncionarioName(item)}`"
            :aria-label="`Remover ${getFuncionarioName(item)} da seleção`"
            @click="removeSelected(item)"
          >
            <v-icon size="19">mdi-close</v-icon>
          </v-btn>
        </article>
      </div>
    </section>

    <v-data-table
      v-if="searched || loading"
      :headers="headers"
      :items="items"
      :search="tableSearch"
      :custom-filter="filtrarValorTabela"
      :items-per-page="itemsPerPage"
      :item-key="itemKey"
      :loading="loading"
      loading-text="Buscando funcionários..."
      no-data-text="Nenhum funcionário encontrado."
      dense
      show-select
      :single-select="!multiple"
      v-model="internalSelected"
      mobile-breakpoint="0"
      class="table busca-funcionario__tabela"
    >
      <template v-slot:no-data>
        <div class="busca-funcionario__estado-vazio">
          <v-icon size="38" color="#adb5bd">mdi-account-search-outline</v-icon>
          <strong>Nenhum funcionário encontrado</strong>
          <span>Revise o nome ou a matrícula e tente novamente.</span>
        </div>
      </template>
      <template v-slot:[`item.servidorCategoria`]="{ item }">
        {{ item.servidorCategoria ? $t(item.servidorCategoria) : "" }}
      </template>
    </v-data-table>

    <div v-else class="busca-funcionario__estado-inicial">
      <v-icon size="42" color="#adb5bd">mdi-account-search-outline</v-icon>
      <div>
        <strong>Comece pela busca</strong>
        <span>Informe o nome ou a matrícula para localizar o funcionário.</span>
      </div>
    </div>
  </v-card>
</template>

<script>
export default {
  name: "BuscaSelecionaFuncionario",
  props: {
    value: {
      type: Array,
      default() {
        return [];
      },
    },
    headers: {
      type: Array,
      required: true,
    },
    dataService: {
      type: Object,
      default: null,
    },
    fetchMethod: {
      type: String,
      default: "getListaFuncionarios",
    },
    fetchFunction: {
      type: Function,
      default: null,
    },
    itemKey: {
      type: String,
      default: "matricula",
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "Buscar funcionário",
    },
    subtitle: {
      type: String,
      default: "Use nome ou matrícula para localizar.",
    },
    infoText: {
      type: String,
      default: "",
    },
    itemsPerPage: {
      type: Number,
      default: 5,
    },
    enableLocalSearch: {
      type: Boolean,
      default: true,
    },
    showSelectionActions: {
      type: Boolean,
      default: true,
    },
    showChips: {
      type: Boolean,
      default: true,
    },
    showSelectedSummary: {
      type: Boolean,
      default: true,
    },
    autoSearch: {
      type: Boolean,
      default: false,
    },
    extraParams: {
      type: Object,
      default() {
        return {};
      },
    },
    labelFormatter: {
      type: Function,
      default: null,
    },
  },

  data() {
    return {
      filters: {
        nome: "",
        matricula: "",
      },
      items: [],
      tableSearch: "",
      loading: false,
      searched: false,
      errorMessage: "",
      searchRequestId: 0,
    };
  },

  computed: {
    internalSelected: {
      get() {
        return Array.isArray(this.value) ? this.value : [];
      },
      set(val) {
        this.$emit("input", Array.isArray(val) ? val : []);
      },
    },
    hasFilters() {
      return Boolean(this.filters.nome || this.filters.matricula);
    },
    showResultCount() {
      return this.searched && !this.loading;
    },
    resultCountLabel() {
      const quantidade = this.items.length;
      return `${quantidade} ${quantidade === 1 ? "resultado" : "resultados"}`;
    },
    selectedCountLabel() {
      const quantidade = this.internalSelected.length;
      return `${quantidade} ${
        quantidade === 1 ? "selecionado" : "selecionados"
      }`;
    },
    showLocalResultFilter() {
      return Boolean(
        this.enableLocalSearch &&
          this.searched &&
          !this.loading &&
          this.items.length
      );
    },
    showSelectedCards() {
      return Boolean(
        this.internalSelected.length &&
          (this.showChips || (!this.multiple && this.showSelectedSummary))
      );
    },
    selectedSectionTitle() {
      return this.internalSelected.length === 1
        ? "Funcionário selecionado"
        : "Funcionários selecionados";
    },
    selectedSectionHint() {
      return this.internalSelected.length === 1
        ? "Confira o funcionário escolhido antes de continuar."
        : "Confira a equipe escolhida antes de continuar.";
    },
    allSelected() {
      if (!Array.isArray(this.items) || !this.items.length) return false;
      const listKeys = new Set(
        this.items.map((item) => this.getItemKey(item)).filter(Boolean)
      );
      if (!listKeys.size) return false;
      const selectedKeys = new Set(
        this.internalSelected
          .map((item) => this.getItemKey(item))
          .filter(Boolean)
      );
      for (const key of listKeys) {
        if (!selectedKeys.has(key)) return false;
      }
      return true;
    },
  },

  mounted() {
    if (this.autoSearch) {
      this.onSearch();
    }
  },

  beforeDestroy() {
    this.searchRequestId += 1;
  },

  methods: {
    filtrarValorTabela(value, search, item) {
      if (value == null || search == null || typeof value === "boolean") {
        return false;
      }

      const termo = String(search).toLocaleLowerCase("pt-BR");
      if (String(value).toLocaleLowerCase("pt-BR").includes(termo)) {
        return true;
      }

      if (
        !this.headers.some((header) => header.value === "servidorCategoria") ||
        value !== item?.servidorCategoria
      ) {
        return false;
      }

      const normalizar = (text) =>
        String(text)
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLocaleLowerCase("pt-BR");
      return normalizar(this.$t(value)).includes(normalizar(search));
    },
    onSearchShortcut(event) {
      const key = String(event?.key || "");
      const ctrlOrCmd = Boolean(event && (event.ctrlKey || event.metaKey));
      const isSearchShortcut = Boolean(
        event &&
          key === "Enter" &&
          ctrlOrCmd &&
          event.shiftKey &&
          !event.altKey &&
          !event.repeat
      );

      if (!isSearchShortcut) return;

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === "function") {
        event.stopImmediatePropagation();
      }
      if (!this.loading) this.onSearch();
    },

    async onSearch() {
      const requestId = (this.searchRequestId += 1);
      this.loading = true;
      this.searched = true;
      this.errorMessage = "";

      const params = {
        ...this.extraParams,
      };
      if (this.filters.nome) params.nome = this.filters.nome;
      if (this.filters.matricula) params.matricula = this.filters.matricula;

      try {
        const result = await this.fetchItems(params);
        if (requestId !== this.searchRequestId) return;
        this.items = this.normalizeResult(result);
      } catch (error) {
        if (requestId !== this.searchRequestId) return;
        this.items = [];
        this.errorMessage = "Erro ao buscar funcionários.";
        console.error("Erro ao buscar funcionários:", error);
        this.$emit("error", error);
      } finally {
        if (requestId === this.searchRequestId) {
          this.loading = false;
        }
      }
    },

    onClear() {
      this.filters.nome = "";
      this.filters.matricula = "";
      this.tableSearch = "";
      this.errorMessage = "";
      this.$emit("cleared");
      this.$nextTick(() => {
        const input = this.$refs.nomeInput;
        if (input && typeof input.focus === "function") {
          input.focus();
        }
      });
    },

    async fetchItems(params) {
      if (this.fetchFunction) {
        return this.fetchFunction(params);
      }
      if (
        this.dataService &&
        typeof this.dataService[this.fetchMethod] === "function"
      ) {
        return this.dataService[this.fetchMethod](params);
      }
      throw new Error(
        "BuscaSelecionaFuncionario: método de busca não configurado"
      );
    },

    normalizeResult(result) {
      if (Array.isArray(result)) return result;
      if (result && Array.isArray(result.data)) return result.data;
      if (result && result.data && Array.isArray(result.data.content)) {
        return result.data.content;
      }
      if (result && Array.isArray(result.content)) return result.content;
      return [];
    },

    selectAll() {
      this.internalSelected = Array.isArray(this.items)
        ? this.items.slice()
        : [];
    },

    clearSelection() {
      this.internalSelected = [];
    },

    removeSelected(item) {
      const keyToRemove = this.getItemKey(item);
      this.internalSelected = this.internalSelected.filter(
        (selected) => this.getItemKey(selected) !== keyToRemove
      );
    },

    getItemKey(item) {
      if (!item) return undefined;
      if (this.itemKey) {
        return this.getByPath(item, this.itemKey);
      }
      return item.id;
    },

    getByPath(obj, path) {
      if (!obj || !path) return undefined;
      return path.split(".").reduce((acc, key) => {
        if (acc && Object.prototype.hasOwnProperty.call(acc, key)) {
          return acc[key];
        }
        return undefined;
      }, obj);
    },

    getFuncionarioData(item) {
      return item && item.funcionario ? item.funcionario : item || {};
    },

    getFuncionarioName(item) {
      const funcionario = this.getFuncionarioData(item);
      const nome =
        funcionario?.pessoa?.nome ||
        funcionario.nomeSocial ||
        funcionario.nome ||
        item?.nomeSocial ||
        item?.nome;
      if (nome) return String(nome);

      const label = this.formatLabel(item);
      const separador = label.indexOf(" - ");
      return separador >= 0 ? label.slice(separador + 3) : label;
    },

    getFuncionarioRegistration(item) {
      const funcionario = this.getFuncionarioData(item);
      const matricula = funcionario.matricula || item?.matricula;
      return matricula === undefined || matricula === null
        ? ""
        : String(matricula);
    },

    getFuncionarioInitials(item) {
      const partes = this.getFuncionarioName(item)
        .trim()
        .split(/\s+/)
        .filter(Boolean);
      if (!partes.length) return "FN";
      const primeira = partes[0].charAt(0);
      const ultima =
        partes.length > 1 ? partes[partes.length - 1].charAt(0) : "";
      return `${primeira}${ultima}`.toLocaleUpperCase("pt-BR");
    },

    formatLabel(item) {
      if (this.labelFormatter) {
        return this.labelFormatter(item);
      }
      const funcionario =
        item && item.funcionario ? item.funcionario : item || {};
      const matricula = funcionario.matricula || item.matricula || "";
      const nome =
        funcionario?.pessoa?.nome ||
        funcionario.nomeSocial ||
        funcionario.nome ||
        item.nomeSocial ||
        item.nome ||
        "";
      if (matricula && nome) return `${matricula} - ${nome}`;
      return nome || matricula || "Funcionário";
    },
  },
};
</script>

<style scoped>
.busca-funcionario {
  overflow: hidden;
  border-color: #e9ecef !important;
  border-radius: 14px;
  background: #fff;
}

.busca-funcionario__cabecalho {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 20px 12px;
  border-bottom: 1px solid #f0f2f5;
  background: linear-gradient(135deg, #fff 0%, #f8f9fa 100%);
}

.busca-funcionario__identificacao {
  display: flex;
  align-items: center;
  min-width: 0;
}

.busca-funcionario__icone {
  flex: 0 0 auto;
  margin-right: 12px;
  background: rgba(94, 114, 228, 0.1);
}

.busca-funcionario__indicadores {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.busca-funcionario__aviso {
  margin: 0 20px 12px;
}

.busca-funcionario__erro {
  margin: 0 20px 12px;
}

.busca-funcionario__filtros {
  margin: 8px 20px 12px;
  padding: 6px 8px;
  border: 1px solid #edf0f5;
  border-radius: 12px;
  background: #f8f9fa;
}

.busca-funcionario__campos {
  margin: -4px;
}

.busca-funcionario__campos > .col {
  padding: 8px;
}

.busca-funcionario__secao-titulo {
  display: flex;
  align-items: center;
  margin-bottom: 2px;
  color: #344767;
  font-size: 0.875rem;
  font-weight: 700;
}

.busca-funcionario__acoes-busca {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.busca-funcionario__botao {
  margin: 0;
}

.busca-funcionario__dica {
  display: flex;
  align-items: center;
  margin: 0 8px 2px;
  color: #8392ab;
  font-size: 0.75rem;
}

.busca-funcionario__resultados-topo {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 20px 20px 12px;
}

.busca-funcionario__filtro-local {
  width: 100%;
  max-width: 360px;
}

.busca-funcionario__selecao-acoes {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 20px 12px;
  padding: 8px 12px;
  border: 1px solid rgba(94, 114, 228, 0.16);
  border-radius: 10px;
  background: rgba(94, 114, 228, 0.05);
}

.busca-funcionario__selecao-contagem,
.busca-funcionario__selecao-botoes {
  display: flex;
  align-items: center;
}

.busca-funcionario__selecao-contagem {
  color: #344767;
  font-size: 0.875rem;
  font-weight: 700;
}

.busca-funcionario__selecao-botoes {
  flex-wrap: wrap;
  justify-content: flex-end;
}

.busca-funcionario__tabela {
  margin: 0 20px 20px;
  border: 1px solid #edf0f5;
  border-radius: 10px;
}

.busca-funcionario__tabela::v-deep .v-data-table__wrapper {
  border-radius: 10px 10px 0 0;
}

.busca-funcionario__estado-inicial,
.busca-funcionario__estado-vazio {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  color: #8392ab;
}

.busca-funcionario__estado-inicial {
  min-height: 112px;
  margin: 0 20px 20px;
  padding: 20px;
  border: 1px dashed #d9dee7;
  border-radius: 12px;
  background: #fbfcfd;
}

.busca-funcionario__estado-vazio {
  min-height: 130px;
  flex-direction: column;
  padding: 24px;
  text-align: center;
}

.busca-funcionario__estado-inicial strong,
.busca-funcionario__estado-inicial span,
.busca-funcionario__estado-vazio strong,
.busca-funcionario__estado-vazio span {
  display: block;
}

.busca-funcionario__estado-inicial strong,
.busca-funcionario__estado-vazio strong {
  color: #344767;
}

.busca-funcionario__selecionados {
  margin: 0 20px 20px;
  padding: 14px;
  border: 1px solid rgba(76, 175, 80, 0.2);
  border-radius: 12px;
  background: linear-gradient(
    135deg,
    rgba(76, 175, 80, 0.07) 0%,
    rgba(255, 255, 255, 0.96) 72%
  );
}

.busca-funcionario__selecionados-cabecalho,
.busca-funcionario__selecionados-titulo {
  display: flex;
  align-items: center;
}

.busca-funcionario__selecionados-cabecalho {
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.busca-funcionario__selecionados-titulo {
  gap: 9px;
  min-width: 0;
}

.busca-funcionario__selecionados-titulo strong,
.busca-funcionario__selecionados-titulo span {
  display: block;
}

.busca-funcionario__selecionados-titulo strong {
  color: #344767;
  font-size: 0.875rem;
}

.busca-funcionario__selecionados-titulo span {
  color: #8392ab;
  font-size: 0.75rem;
}

.busca-funcionario__selecionados-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 10px;
  max-height: 244px;
  padding: 2px 4px 2px 2px;
  overflow-y: auto;
}

.busca-funcionario__selecionado-card {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 0;
  min-height: 64px;
  padding: 10px 8px 10px 11px;
  border: 1px solid #e9ecef;
  border-left: 3px solid #4caf50;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 2px 7px rgba(50, 71, 103, 0.06);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.busca-funcionario__selecionado-card:hover {
  border-color: rgba(76, 175, 80, 0.42);
  box-shadow: 0 4px 12px rgba(50, 71, 103, 0.1);
  transform: translateY(-1px);
}

.busca-funcionario__selecionado-avatar {
  position: relative;
  flex: 0 0 auto;
}

.busca-funcionario__selecionado-avatar .v-avatar {
  color: #4f60c7;
  font-size: 0.8rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  background: rgba(94, 114, 228, 0.12);
}

.busca-funcionario__selecionado-check {
  position: absolute;
  right: -2px;
  bottom: -1px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 17px;
  height: 17px;
  border: 2px solid #fff;
  border-radius: 50%;
  background: #4caf50;
}

.busca-funcionario__selecionado-conteudo {
  min-width: 0;
}

.busca-funcionario__selecionado-nome {
  display: block;
  overflow: hidden;
  color: #344767;
  font-size: 0.875rem;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.busca-funcionario__selecionado-matricula {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  color: #8392ab;
  font-size: 0.75rem;
  line-height: 1.2;
}

.busca-funcionario__selecionado-matricula .v-icon {
  color: #8392ab;
}

.busca-funcionario__selecionado-remover {
  flex: 0 0 auto;
  margin-left: auto;
  background: rgba(245, 54, 92, 0.07);
}

.min-width-0 {
  min-width: 0;
}

@media (max-width: 960px) {
  .busca-funcionario__acoes-busca {
    justify-content: flex-start;
  }
}

@media (max-width: 600px) {
  .busca-funcionario__cabecalho,
  .busca-funcionario__resultados-topo,
  .busca-funcionario__selecao-acoes {
    align-items: stretch;
    flex-direction: column;
  }

  .busca-funcionario__cabecalho,
  .busca-funcionario__filtros {
    margin: 0;
  }

  .busca-funcionario__cabecalho {
    padding: 16px;
  }

  .busca-funcionario__filtros {
    border-right: 0;
    border-left: 0;
    border-radius: 0;
  }

  .busca-funcionario__aviso,
  .busca-funcionario__erro,
  .busca-funcionario__resultados-topo,
  .busca-funcionario__selecao-acoes,
  .busca-funcionario__tabela,
  .busca-funcionario__estado-inicial,
  .busca-funcionario__selecionados {
    margin-right: 12px;
    margin-left: 12px;
  }

  .busca-funcionario__indicadores,
  .busca-funcionario__selecao-botoes {
    justify-content: flex-start;
  }

  .busca-funcionario__acoes-busca {
    align-items: stretch;
    flex-direction: column;
  }

  .busca-funcionario__botao {
    width: 100%;
  }

  .busca-funcionario__filtro-local {
    max-width: none;
  }

  .busca-funcionario__estado-inicial {
    align-items: center;
    flex-direction: column;
    text-align: center;
  }

  .busca-funcionario__selecionados-cabecalho {
    align-items: flex-start;
  }

  .busca-funcionario__selecionados-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
